import { ReactNode } from 'react';

import { Box, Stack } from '@mui/material';
import clsx from 'clsx';

import { tokens } from '../../../theme/generated/tokens';
import { classNamesFor } from '../../../utils/classNames';
import { RowInset, RowInsetProps } from '../../containers/RowInset';
import { TypographyOrNode } from '../../utils';

const classNamesFactory = classNamesFor('DialogHeader');

const classNames = {
  root: classNamesFactory.root(),
};

export type DialogHeaderProps = {
  /**
   * Title, to be displayed in the header
   */
  title: ReactNode;
  /**
   * Metadata content displayed below the title
   */
  metadata?: ReactNode;
  /**
   * Show the divider at the bottom of the header
   */
  divider?: boolean;
} & Omit<RowInsetProps, 'startContent' | 'title' | 'dividerBottom'>;

export const DialogHeader = ({
  title,
  metadata,
  divider,
  className,
  ...rowInsetProps
}: DialogHeaderProps) => {
  const titleEl = (
    <div data-testid="dialog-title">
      <TypographyOrNode
        variant="h5"
        sx={{
          wordBreak: 'break-all',
          color: tokens.theme.fg.structural.primary,
        }}
      >
        {title}
      </TypographyOrNode>
    </div>
  );
  return (
    <RowInset
      startContent={
        metadata != null ? (
          <Stack>
            {titleEl}
            <Box
              sx={{
                typography: 'A1',
                color: tokens.theme.fg.structural.secondary,
              }}
              data-testid="dialog-metadata"
            >
              {metadata}
            </Box>
          </Stack>
        ) : (
          titleEl
        )
      }
      py={16}
      // Close icon is displayed in the top-right corner of the dialog
      // Since the header is optional, but we don't have dialogs without a close icon,
      // it is simpler to absolutely position the close icon and add padding in the header
      pr={52}
      dividerBottom={divider}
      className={clsx(classNames.root, className)}
      testId="dialog-header" // Not exposing testId intentionally, to have a stable test ID in the driver. There should always be only one dialog header in the parent driver context
      alignItems="center"
      {...rowInsetProps}
    />
  );
};

DialogHeader.classNames = classNames;
